import { SCHEMA_VERSION, Scenario, type Scenario as ScenarioType } from "@radar-sim/schema";

/**
 * Pipeline of versioned migrations applied in sequence to bring a scenario
 * from any prior schema version up to {@link SCHEMA_VERSION}. Each entry
 * accepts the previous-version shape and returns the next-version shape.
 *
 * v1 is currently the only version, so this list is empty. When v2 lands:
 *   1. Retain the v1 Zod schema in @radar-sim/schema for migration.
 *   2. Add a `migrateV1ToV2(input): unknown` function here.
 *   3. Bundled scenarios get re-saved at v2 on the next release.
 */
const MIGRATIONS: Array<{
  from: number;
  to: number;
  fn: (input: unknown) => unknown;
}> = [];

export interface MigrationResult {
  scenario: ScenarioType;
  appliedMigrations: ReadonlyArray<`v${number}->v${number}`>;
}

export class ScenarioMigrationError extends Error {
  public override readonly cause?: unknown;
  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = "ScenarioMigrationError";
    if (cause !== undefined) this.cause = cause;
  }
}

/**
 * Migrate `raw` (a parsed JSON value of unknown version) to the current
 * schema version, then validate. Returns the validated scenario plus the
 * list of migrations applied (handy for telemetry on legacy uploads).
 */
export function migrateAndParse(raw: unknown): MigrationResult {
  if (
    raw === null ||
    typeof raw !== "object" ||
    !("schemaVersion" in raw) ||
    typeof (raw as { schemaVersion: unknown }).schemaVersion !== "number"
  ) {
    throw new ScenarioMigrationError(
      "scenario is missing a numeric schemaVersion",
    );
  }
  let current = raw as { schemaVersion: number };
  const applied: Array<`v${number}->v${number}`> = [];
  while (current.schemaVersion < SCHEMA_VERSION) {
    const step = MIGRATIONS.find((m) => m.from === current.schemaVersion);
    if (step === undefined) {
      throw new ScenarioMigrationError(
        `no migration registered for schemaVersion ${current.schemaVersion}`,
      );
    }
    try {
      current = step.fn(current) as { schemaVersion: number };
    } catch (e) {
      throw new ScenarioMigrationError(
        `migration v${step.from}→v${step.to} failed`,
        e,
      );
    }
    applied.push(`v${step.from}->v${step.to}`);
  }
  if (current.schemaVersion > SCHEMA_VERSION) {
    throw new ScenarioMigrationError(
      `scenario schemaVersion ${current.schemaVersion} is newer than supported (${SCHEMA_VERSION}); upgrade the simulator`,
    );
  }
  const parsed = Scenario.safeParse(current);
  if (!parsed.success) {
    throw new ScenarioMigrationError(
      `scenario failed schema validation after migration: ${parsed.error.message}`,
      parsed.error,
    );
  }
  return { scenario: parsed.data, appliedMigrations: applied };
}

/**
 * Test/devtool hook for registering ad-hoc migrations. Returns a removal
 * function. Not exported from the package barrel — internal use only.
 */
export function _registerMigration(
  from: number,
  to: number,
  fn: (input: unknown) => unknown,
): () => void {
  const entry = { from, to, fn };
  MIGRATIONS.push(entry);
  return () => {
    const i = MIGRATIONS.indexOf(entry);
    if (i >= 0) MIGRATIONS.splice(i, 1);
  };
}
